import { motion } from "framer-motion";
import { Percent, ScanSearch, Tag, Trophy } from "lucide-react";
import { IconBadge } from "@/components/ui/icon-badge";
import { useState, useEffect } from "react";
import { useModelSummaryStatsQuery } from "../../api/vercel";

// Global flag to track if initial animation has completed
let globalAnimationCompleted = false;

// Animated number component
const AnimatedNumber = ({ value, isCurrency = false, isPercentage = false, isInteger = false, duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(globalAnimationCompleted ? value : 0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (value !== null && value !== undefined) {
      if (!globalAnimationCompleted) {
        // Only animate on first page load
        setIsAnimating(true);
        const startTime = Date.now();

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Easing function for smooth animation
          const easeOutQuart = 1 - Math.pow(1 - progress, 4);

          const currentValue = value * easeOutQuart;
          setDisplayValue(currentValue);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            setDisplayValue(value);
            setIsAnimating(false);
            globalAnimationCompleted = true;
          }
        };

        requestAnimationFrame(animate);
      } else {
        // After initial animation, just set the value directly
        setDisplayValue(value);
      }
    }
  }, [value, duration]);

  if (value === null || value === undefined) {
    return <span className="text-lg sm:text-3xl md:text-4xl font-bold text-gray-900 opacity-5">0</span>;
  }

  const formatValue = (val) => {
    if (isCurrency) {
      return `$${val.toFixed(2)}`;
    } else if (isPercentage) {
      return `${val.toFixed(2)}%`;
    } else if (isInteger) {
      return Math.round(val).toLocaleString();
    } else {
      return val.toFixed(2);
    }
  };

  return (
    <span className="text-lg sm:text-3xl md:text-4xl font-bold text-gray-900">
      {formatValue(displayValue)}
    </span>
  );
};

const FlipCard = ({ stat, isFlipped, onClick, isAutoFlip = false, onPause, isHoverPaused = false, isClickPaused = false, onClickPause }) => {
  const canClick = stat.canClickFlip && !stat.isAutoFlip;
  const isAnyPaused = isHoverPaused || isClickPaused;

  const CardContent = ({ data, isBack = false }) => (
    <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-brand-pink/20 h-full flex flex-col items-center text-center relative">
      <IconBadge
        icon={data.icon}
        className="mb-4 transition-transform duration-300 group-hover:scale-110"
      />

      <div className="mb-2">
        <AnimatedNumber
          value={data.value}
          isCurrency={data.isCurrency}
          isPercentage={data.isPercentage}
          isInteger={data.isInteger}
        />
      </div>

      <h3 className="text-xs md:text-lg font-semibold text-gray-900 mb-2">
        {data.label}
      </h3>

      {/* Sub Label - appears below the main label if provided */}
      {data.subLabel && (
        <p
          className="text-xxs text-gray-500 mb-2 uppercase"
          dangerouslySetInnerHTML={{ __html: data.subLabel }}
        />
      )}

      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-brand-pink/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />


    </div>
  );

  return (
    <div
      className="flip-card w-full h-full cursor-pointer rounded-xl transition-all duration-200"
      onClick={() => {
        if (canClick) {
          onClick();
        } else {
          onClickPause();
        }
      }}
      style={{
        perspective: "1000px",
        position: "relative",
      }}
    >
      <div
        className="flip-card-inner relative w-full h-full transition-transform duration-500"
        style={{
          transformStyle: "preserve-3d",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Front of card */}
        <div
          className="flip-card-front relative inset-0 w-full h-full backface-hidden"
          style={{ backfaceVisibility: "hidden" }}
        >
          <CardContent data={stat} />
        </div>

        {/* Back of card */}
        <div
          className="flip-card-back absolute inset-0 w-full h-full backface-hidden"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          {isAutoFlip && stat.backData ? (
            // Auto-flip cards show the same layout with different data
            <CardContent data={stat.backData} isBack={true} />
          ) : (
            // Manual-flip cards show description text
            <div className="bg-white rounded-xl xs:p-4 p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-brand-pink/20 h-full flex items-center justify-center">
              <p
                className="xs:text-sm text-lg text-gray-600 leading-relaxed text-center"
                dangerouslySetInnerHTML={{ __html: stat.description }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function StatsSection() {
  // Calculate days since Feb 5, 2025
  const startDate = new Date('2025-02-05');
  const today = new Date();
  const daysSince = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));

  // Format date as "Monday Feb 26, 2025"
  const formattedDate = startDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  // State to track which cards are flipped
  const [flippedCards, setFlippedCards] = useState({});
  const [autoFlipState, setAutoFlipState] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [clickPaused, setClickPaused] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Fetch model summary stats data
  const { data: modelStatsData, isLoading, error } = useModelSummaryStatsQuery({
    modelNames: ["HayiAuto"],
    limit: 1,
    enabled: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Detect touch device on mount
  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };

    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);

    return () => window.removeEventListener('resize', checkTouchDevice);
  }, []);

  // Auto-flip timer for auto-flip cards
  useEffect(() => {
    const interval = setInterval(() => {
      // On touch devices, only respect click pause, ignore hover pause
      const shouldPause = isTouchDevice ? clickPaused : (isPaused || clickPaused);
      if (!shouldPause) {
        setAutoFlipState(prev => !prev);
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [isPaused, clickPaused, isTouchDevice]);

  // Function to handle card flip
  const handleCardFlip = (index) => {
    setFlippedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Function to handle card hover pause (only for non-touch devices)
  const handleCardPause = (shouldPause) => {
    if (!isTouchDevice) {
      setIsPaused(shouldPause);
    }
  };

  // Function to handle click pause/resume
  const handleClickPause = () => {
    setClickPaused(prev => !prev);
  };

  // Calculate stats from API data
  const calculateStats = () => {
    if (!modelStatsData?.data || modelStatsData.data.length === 0) {
      return null;
    }

    const latestData = modelStatsData.data[0];

    // Use raceTime if available, otherwise fall back to early
    const statsSource = latestData.raceTime || latestData.early;

    if (!statsSource?.p1) {
      return null;
    }

    const p1 = statsSource.p1;
    const p2 = statsSource.p2 || { bets: 0, wins: 0, totalReturns: 0, strikeRate: 0 };

    // P1 stats (front of auto-flip cards)
    const p1AverageWinPrice = p1.bets > 0 ? p1.totalReturns / p1.wins : 0;

    // P1+P2 combined stats (back of auto-flip cards)
    const combinedBets = p1.bets + p2.bets;
    const combinedWins = p1.wins + p2.wins;
    const combinedReturns = p1.totalReturns + p2.totalReturns;
    const combinedWinRate = p1.strikeRate + p2.strikeRate; // Sum of P1 and P2 win rates
    const combinedAverageWinPrice = combinedWins > 0 ? combinedReturns / combinedWins : 0;

    return {
      // P1 stats (front)
      racesAnalysed: p1.bets,
      winRate: p1.strikeRate,
      winnersTipped: p1.wins,
      averageWinPrice: p1AverageWinPrice,

      // P1+P2 combined stats (back)
      combined: {
        racesAnalysed: combinedBets,
        winRate: combinedWinRate,
        winnersTipped: combinedWins,
        averageWinPrice: combinedAverageWinPrice,
      }
    };
  };

  const calculatedStats = calculateStats();

  const stats = [
    {
      icon: ScanSearch,
      value: calculatedStats?.racesAnalysed,
      label: "Races Analysed",
      description: `Races analysed since <span class="text-brand-pink font-bold">${formattedDate}</span>`,
      isCurrency: false,
      isPercentage: false,
      isInteger: true,
      isAutoFlip: false,
      canClickFlip: false,
    },
    {
      icon: Percent,
      value: calculatedStats?.winRate,
      label: "Win Rate",
      subLabel: `Based on Top Tip`,
      description: `From Lexi's <span class="text-brand-pink font-bold">Late Top Tip</span>`,
      isCurrency: false,
      isPercentage: true,
      isInteger: false,
      isAutoFlip: true,
      canClickFlip: false,
      backData: {
        icon: Percent,
        value: calculatedStats?.combined?.winRate,
        label: "Win Rate",
        subLabel: `1 of top 2 wins`,
        isCurrency: false,
        isPercentage: true,
        isInteger: false,
      }
    },
    {
      icon: Trophy,
      value: calculatedStats?.winnersTipped,
      label: "Winners Tipped",
      subLabel: "Based on Top Tip",
      description: `Total of Lexi's <span class="text-brand-pink font-bold">Late Top Tip</span> that won`,
      isCurrency: false,
      isPercentage: false,
      isInteger: true,
      isAutoFlip: true,
      canClickFlip: false,
      backData: {
        icon: Trophy,
        value: calculatedStats?.combined?.winnersTipped,
        label: "Winners Tipped",
        subLabel: "Based on 2 Tips",
        isCurrency: false,
        isPercentage: false,
        isInteger: true,
      }
    },
    {
      icon: Tag,
      value: calculatedStats?.averageWinPrice,
      label: "Avg. Win Price",
      subLabel: "Based on Top Tip",
      description: `Average win amount for Lexi's <span class="text-brand-pink font-bold">Late Top Tip</span>`,
      isCurrency: true,
      isPercentage: false,
      isInteger: false,
      isAutoFlip: true,
      canClickFlip: false,
      backData: {
        icon: Tag,
        value: calculatedStats?.combined?.averageWinPrice,
        label: "Avg. Win Price",
        subLabel: "Based on 2 Tips",
        isCurrency: true,
        isPercentage: false,
        isInteger: false,
      }
    },
  ];


  // Show error state if there's an error
  if (error) {
    console.error("Error loading model stats:", error);
    // Fall back to default stats or show error message
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="relative xs:pt-2 pt-4 pb-6 md:pb-8 2xl:pb-12 bg-white overflow-visible">
      {/* Background Decorations */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Dot Pattern */}
        <div
          className="absolute inset-0 opacity-50"
        // style={{
        //   backgroundImage: `radial-gradient(circle at 1px 1px, #e5e5e5 1px, transparent 0)`,
        //   backgroundSize: "24px 24px",
        // }}
        />
        {/* Center Radial Gradient */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-100"
          style={{
            background:
              "radial-gradient(50% 50% at 50% 50%, rgba(255, 20, 147, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
          }}
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-0 md:px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >

        </motion.div>

        {/* Cards wrapper with pause indicator */}
        <div
          className="relative max-w-7xl mx-auto px-0 md:px-4"
          onMouseEnter={() => handleCardPause(true)}
          onMouseLeave={() => handleCardPause(false)}
        >
          {/* Global pause indicator */}
          {((isPaused && !isTouchDevice) || clickPaused) && (
            <div className="absolute -inset-4 border-2 border-brand-pink/30 rounded-2xl bg-brand-pink/5 pointer-events-none transition-all duration-300 z-30">
              <div className="absolute top-3 right-3 bg-gray-800/90 text-white text-sm px-3 py-1.5 rounded-full z-40">
                {clickPaused ? 'Click any card to resume' : 'Paused'}
              </div>
            </div>
          )}

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-8 relative z-10"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="flip-card-container group w-full min-w-[120px] xl:min-w-[180px]"
              >
                <FlipCard
                  stat={stat}
                  isFlipped={stat.isAutoFlip ? autoFlipState : flippedCards[index]}
                  onClick={() => stat.canClickFlip && !stat.isAutoFlip && handleCardFlip(index)}
                  isAutoFlip={stat.isAutoFlip}
                  onPause={handleCardPause}
                  isHoverPaused={isPaused}
                  isClickPaused={clickPaused}
                  onClickPause={handleClickPause}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>

      </div>
    </section>
  );
}

export default StatsSection; 